import { io, Socket } from 'socket.io-client';
import { Message, User } from '../types';

class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnected = false;
  private currentRoomId: string | null = null;
  private currentUserId: string | null = null;

  // Event callbacks
  private onMessageCallback: ((message: Message) => void) | null = null;
  private onUserJoinedCallback: ((user: User) => void) | null = null;
  private onUserLeftCallback: ((user: User) => void) | null = null;
  private onTypingCallback: ((userId: string, isTyping: boolean) => void) | null = null;
  private onConnectionCallback: ((connected: boolean) => void) | null = null;
  private onErrorCallback: ((error: string) => void) | null = null;

  constructor() {
    this.setupEventListeners();
  }

  // Initialize connection
  connect(token?: string): void {
    const serverUrl = import.meta.env.REACT_APP_WS_URL || 'http://localhost:3001';
    
    const options: any = {
      transports: ['websocket', 'polling'],
      timeout: 20000,
    };

    // Add auth token if provided
    if (token) {
      options.auth = { token };
    }

    this.socket = io(serverUrl, options);
    this.setupSocketEventListeners();
  }

  // Disconnect from server
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentRoomId = null;
      this.currentUserId = null;
      this.reconnectAttempts = 0;
    }
  }

  // Join a chat room
  joinRoom(roomId: string, userId: string): void {
    if (!this.socket || !this.isConnected) {
      console.error('Socket not connected');
      return;
    }

    // Leave current room if any
    if (this.currentRoomId && this.currentRoomId !== roomId) {
      this.leaveRoom();
    }

    this.currentRoomId = roomId;
    this.currentUserId = userId;
    
    this.socket.emit('joinRoom', { roomId, userId });
  }

  // Leave current room
  leaveRoom(): void {
    if (!this.socket || !this.currentRoomId) {
      return;
    }

    this.socket.emit('leaveRoom', { 
      roomId: this.currentRoomId, 
      userId: this.currentUserId 
    });
    
    this.currentRoomId = null;
    this.currentUserId = null;
  }

  // Send message
  sendMessage(message: Message): void {
    if (!this.socket || !this.isConnected) {
      console.error('Socket not connected');
      return;
    }

    if (!this.currentRoomId) {
      console.error('Not in a room');
      return;
    }

    this.socket.emit('sendMessage', {
      ...message,
      roomId: this.currentRoomId,
    });
  }

  // Send typing indicator
  sendTyping(isTyping: boolean): void {
    if (!this.socket || !this.isConnected || !this.currentRoomId || !this.currentUserId) {
      return;
    }

    this.socket.emit(isTyping ? 'typing' : 'stopTyping', {
      roomId: this.currentRoomId,
      userId: this.currentUserId,
    });
  }

  // Setup socket event listeners
  private setupSocketEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('Connected to server');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.onConnectionCallback?.(true);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from server:', reason);
      this.isConnected = false;
      this.onConnectionCallback?.(false);
      
      // Attempt to reconnect if not a manual disconnect
      if (reason !== 'io client disconnect') {
        this.attemptReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      this.isConnected = false;
      this.onErrorCallback?.(error.message);
      this.attemptReconnect();
    });

    // Message events
    this.socket.on('newMessage', (message: Message) => {
      this.onMessageCallback?.(message);
    });

    this.socket.on('messageError', (error: { message: string }) => {
      this.onErrorCallback?.(error.message);
    });

    // Room events
    this.socket.on('userJoined', (user: User) => {
      this.onUserJoinedCallback?.(user);
    });

    this.socket.on('userLeft', (user: User) => {
      this.onUserLeftCallback?.(user);
    });

    // Typing events
    this.socket.on('userTyping', (data: { userId: string }) => {
      this.onTypingCallback?.(data.userId, true);
    });

    this.socket.on('userStoppedTyping', (data: { userId: string }) => {
      this.onTypingCallback?.(data.userId, false);
    });

    // Error events
    this.socket.on('error', (error: { message: string }) => {
      console.error('Socket error:', error);
      this.onErrorCallback?.(error.message);
    });
  }

  // Setup global event listeners
  private setupEventListeners(): void {
    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Page is hidden, reduce activity
        if (this.socket && this.isConnected) {
          this.socket.emit('userInactive');
        }
      } else {
        // Page is visible, resume activity
        if (this.socket && this.isConnected) {
          this.socket.emit('userActive');
        }
      }
    });

    // Handle before unload
    window.addEventListener('beforeunload', () => {
      this.disconnect();
    });
  }

  // Attempt to reconnect
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.onErrorCallback?.('Connection failed. Please refresh the page.');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);

    setTimeout(() => {
      if (this.socket && !this.isConnected) {
        this.socket.connect();
      }
    }, delay);
  }

  // Event callback setters
  onMessage(callback: (message: Message) => void): void {
    this.onMessageCallback = callback;
  }

  onUserJoined(callback: (user: User) => void): void {
    this.onUserJoinedCallback = callback;
  }

  onUserLeft(callback: (user: User) => void): void {
    this.onUserLeftCallback = callback;
  }

  onTyping(callback: (userId: string, isTyping: boolean) => void): void {
    this.onTypingCallback = callback;
  }

  onConnection(callback: (connected: boolean) => void): void {
    this.onConnectionCallback = callback;
  }

  onError(callback: (error: string) => void): void {
    this.onErrorCallback = callback;
  }

  // Getters
  getIsConnected(): boolean {
    return this.isConnected;
  }

  getCurrentRoomId(): string | null {
    return this.currentRoomId;
  }

  getSocket(): Socket | null {
    return this.socket;
  }
}

const webSocketService = new WebSocketService();
export default webSocketService; 